// @givmo/passkeys — the account reset's words on the web (AS-WEB13).
//
// Every sentence is the apps' approved wording (D-AS-29, as iOS and Android carry it after their
// wording rows), with the web's place name where the apps name theirs: the apps' "Passkeys in
// Settings", "Passkeys" and "Settings › Security" are "Sign-in & security" here — the card's title on
// the charity console, the employer portal's page, and the account-menu row on both (ruled for cut
// 1003c, decision 5's intent: name where the thing actually lives). The cancel lives on that card's
// notice, never a "Today tab", and a desktop has no tap, so the apps' "Tap" is "Select".
//
// NAMED BY WHAT THE ACCOUNT HAS (decision 2): the passkey door ("Can't use a passkey?", "Lost your
// passkey?") opens the PASSKEY reset, worded as the apps' Reset your passkeys screens, with their
// authenticator sentence when the account also holds one; "Lost your authenticator?" opens the
// TWO-FACTOR reset, worded as the apps' two-factor reset. Both are the same backend reset, which
// removes every second factor when it completes.
//
// APPROVED (D-AS-35, the round-2 sweep): every line here not marked with a later round below. Two
// deviations from the apps, both approved:
//   - the two-factor reset's finish reads "Two-factor authentication is off" / "…You'll be asked to
//     sign in again, without a code. Then you can set them up again in Sign-in & security." (C4,
//     #15) in place of the apps' "…Sign in again to continue, then set them up again from Settings.";
//   - the two-factor reset's started screen ends with the passkey reset's cancel sentence, since a
//     signed-in person can cancel right there (C5).
// Signed in, the code step asks rather than claims (fix 10): "Check your inbox for a 6-digit code
// from Givmo, and enter it below." — the address is the person's own, and a code is not always sent.
// ⚠ THE BACKEND REMOVING THE AUTHENTICATOR ENDS EVERY FIREBASE SESSION (cut 1003g, measured on dev: a
// recovery code's `unenroll_totp_factors` is Identity Toolkit's `accounts:update` with an empty
// enrolment list, and every Firebase session of the account then answers `auth/user-token-expired` —
// one signed in before the authenticator existed included; a new sign-in needs no code). The reset's
// completion makes the same call when the account held one (inferred, not measured: no reset
// completes on dev), so every finish that turns two-factor off says "You'll be asked to sign in again,
// without a code."; a passkey-only reset deletes passkeys from Givmo's own store, writes nothing to
// Identity Platform, and ends no session, so it does not. (The card's own Turn off is a client
// withdraw, which keeps the calling session and ends the others: measured, and it says nothing.)
// ⚠ In flux: Account Security & SSO Plan item AS-BE63. Until it lands the suite session survives such
// a removal (measured), so a console's next cold boot signs the person back in unasked; the lines stand
// because authenticator apps stay dark in staging and production until it does.
// And one of honesty, adding no word: where this account would find no authenticator setup on the
// web (D-AS-26 N2 offers it only to Google, Apple and Microsoft sign-ins, and only while the backend
// serves authenticator apps), a finish never tells it to turn one on again "in Sign-in & security":
// the passkey reset's sentence stops before that clause, and the two-factor reset's points to a
// passkey instead (fix 9).
//
// HONEST ABOUT PASSKEYS (AS-WEB18; APPROVED where it adds or drops words, copy round 3's E9 as
// written, D-AS-39): the two-factor reset's "That also removes any passkeys on your account." is
// said only where the account may hold one — an account the server says holds none (a Board
// director with an authenticator and no passkey: B2d) is told nothing about passkeys, and its
// finish reads the approved sentences without the passkeys clause. While the passkey list is
// unknown the apps' "any passkeys" stands, true either way.
//
// FINISHING (AS-WEB17; APPROVED, copy round 3's E6–E8 as written, D-AS-39): a reset opened while
// one is armed and its wait has run starts on its finish ("Finish the reset"), never on the
// request's "There's a 24-hour wait"; the card's notice offers "Finish the reset" and says "It can
// be finished now…" once the wait is over. Decision 3's deadline sentence is the approved one.
//
// ⚠ "24-hour" is the backend's `ACCOUNT_SECURITY_FACTOR_RESET_DELAY_HOURS` default; no environment
// overrides it (read 2026-10-02 by the apps' wording rows). A change to that knob makes the request
// screens untrue, as it does the apps'.

import type { StepUpLostLines } from '@givmo/step-up';

/** Which reset the screens name. */
export type FactorResetKind = 'passkey' | 'twoFactor';

/** What the words depend on beyond the kind. */
export interface FactorResetWordsOptions {
  /**
   * Whether the account holds an authenticator app (the server's `mfa.totp_enrolled`), which a
   * finished passkey reset turns off too; `undefined` while unknown, when the words disclose it
   * conditionally rather than stay silent or claim it.
   */
  alsoTurnsOffTwoFactor: boolean | undefined;
  /** Whether this account can set an authenticator app up again on the web. */
  authenticatorOffered: boolean;
  /**
   * Whether the account holds a passkey (`GET /users/me/passkeys`), which a finished two-factor
   * reset removes too; `undefined` while unknown, when the words keep the apps' "any passkeys" (true
   * either way). Known `false`, they say nothing of passkeys (AS-WEB18).
   */
  holdsPasskey?: boolean | undefined;
}

/** Every line one reset's screens show. */
export interface FactorResetWords {
  title: string;
  /** The request step's paragraphs. */
  request: readonly string[];
  /** The armed outcome's paragraphs, given the server's deadline (`null` when it sent none). */
  started: (executesAt: Date | null) => readonly string[];
  completedTitle: string;
  completed: string;
  /** The first step's title when the armed reset's wait has run (AS-WEB17). */
  finishTitle: string;
  /** Its paragraphs: the code that finishes it, and what finishing removes. */
  finish: readonly string[];
  /** A finishing code the server answered "not yet" (its deadline): the title. */
  notYetTitle: string;
  /** …and its paragraphs, given the server's deadline (`null` when it sent none). */
  notYet: (executesAt: Date | null) => readonly string[];
}

export const FACTOR_RESET_PLACE = 'Sign-in & security';

/** "Changed your mind?" — where the cancel lives on the web. */
export const FACTOR_RESET_CANCEL_LINE = `Changed your mind? Select “Wasn’t me — cancel it” in ${FACTOR_RESET_PLACE}.`;

/** The shared lines both resets reuse (the apps' shipped reset steps). */
export const FACTOR_RESET_STEP_WORDS = {
  emailLabel: 'Account email',
  request: 'Email me a code',
  haveCode: 'I already have a code',
  verifyTitle: 'Enter your emailed code',
  // D-AS-35 fix 10: signed in, the address is the person's own; asks rather than claims.
  verifyLine: 'Check your inbox for a 6-digit code from Givmo, and enter it below.',
  codeLabel: '6-digit code',
  verify: 'Continue',
  resend: 'Email me a new code',
  startedTitle: 'Reset started',
  done: 'Done',
  back: 'Back',
  cancel: 'Cancel',
  /** The apps' line for a reset the backend does not serve here (404). */
  unavailable: 'Recovery isn’t available right now. Please try again later.',
} as const;

const DATE = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const TIME = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });

/** "Oct 3, 2026 at 2:15 PM" in the reader's own zone, as the apps print the deadline. */
export function formatFactorResetDeadline(date: Date): string {
  return `${DATE.format(date)} at ${TIME.format(date)}`;
}

/** Removing the authenticator ends the account's Firebase sessions (see the header): #15, approved. */
const SIGN_IN_AGAIN = 'You’ll be asked to sign in again, without a code.';

/** Decision 3's approved deadline sentence (D-AS-29): the card's notice says it too. */
function deadlineSentence(executesAt: Date | null): string {
  return executesAt !== null
    ? `From ${formatFactorResetDeadline(executesAt)} it can be finished with a new code from your email, unless you cancel.`
    : 'After the waiting period it can be finished with a new code from your email, unless you cancel.';
}

/**
 * A finishing code the server answered "not yet" (APPROVED, D-AS-39 E7): its own date, never ours.
 */
const NOT_YET_TITLE = 'The wait isn’t over yet';

/** The finish step's title and the card's button (AS-WEB17; APPROVED, D-AS-39 E6 and E8). */
export const FACTOR_RESET_FINISH = 'Finish the reset';

function passkeyWords({
  alsoTurnsOffTwoFactor,
  authenticatorOffered,
}: FactorResetWordsOptions): FactorResetWords {
  const request = [
    'Can’t use any of your passkeys? We’ll email a code to your Givmo address.',
    'There’s a 24-hour wait, so no one can do this quietly. We’ll email you when it starts, so you can cancel it.',
    `When the wait is over, come back to ${FACTOR_RESET_PLACE} and enter a new emailed code to remove your passkeys.`,
  ];
  if (alsoTurnsOffTwoFactor === true) request.push('It also turns off two-factor authentication.');
  if (alsoTurnsOffTwoFactor === undefined) {
    request.push('If two-factor authentication is on, it turns that off too.');
  }
  const turnOnAgain = authenticatorOffered
    ? ` — you can turn it on again in ${FACTOR_RESET_PLACE}.`
    : '.';
  // #15 (approved): an account that held an authenticator is signed out by its removal, so the
  // finish leads with that; a passkey-only reset ends no session and keeps "now".
  const completed =
    alsoTurnsOffTwoFactor === false
      ? 'You can set up a new passkey now.'
      : alsoTurnsOffTwoFactor === true
        ? `${SIGN_IN_AGAIN} Then you can set up a new passkey. Two-factor authentication is off too${turnOnAgain}`
        : 'If two-factor authentication was on, it’s off too, and you’ll be asked to sign in again, without a code. You can set up a new passkey once you’re signed in.';
  return {
    title: 'Reset your passkeys',
    request,
    started: (executesAt) => [
      `Your passkeys keep working until you finish. ${
        executesAt !== null
          ? `From ${formatFactorResetDeadline(executesAt)}, come back`
          : 'After the waiting period, come back'
      } to ${FACTOR_RESET_PLACE} and enter a new emailed code to remove them. We’ve emailed you in case it wasn’t you.`,
      FACTOR_RESET_CANCEL_LINE,
    ],
    completedTitle: 'Your passkeys were removed',
    completed,
    finishTitle: FACTOR_RESET_FINISH,
    // APPROVED (AS-WEB17; D-AS-39 E6, as written): the finish step's paragraphs.
    finish: [
      'The wait is over. We’ll email a code to your Givmo address.',
      `Enter it to remove your passkeys.${
        alsoTurnsOffTwoFactor === true
          ? ' It also turns off two-factor authentication.'
          : alsoTurnsOffTwoFactor === undefined
            ? ' If two-factor authentication is on, it turns that off too.'
            : ''
      }`,
    ],
    notYetTitle: NOT_YET_TITLE,
    notYet: (executesAt) => [deadlineSentence(executesAt), FACTOR_RESET_CANCEL_LINE],
  };
}

function twoFactorWords({
  authenticatorOffered,
  holdsPasskey,
}: FactorResetWordsOptions): FactorResetWords {
  // The apps' approved sentence, said only where the account may hold a passkey (AS-WEB18; leaving it
  // out is APPROVED, D-AS-39 E9, as written).
  const passkeysToo =
    holdsPasskey === false ? '' : ' That also removes any passkeys on your account.';
  // The account held an authenticator (this is "Lost your authenticator?"'s reset): its removal ends
  // every Firebase session, so the finish says so (C4 with #15; fix 9 points an account the web offers no
  // authenticator to a passkey). An account known to hold no passkey reads the same without the
  // passkeys clause (AS-WEB18; APPROVED, D-AS-39 E9).
  const nextStep = authenticatorOffered
    ? holdsPasskey === false
      ? `Then you can set it up again in ${FACTOR_RESET_PLACE}.`
      : `Then you can set them up again in ${FACTOR_RESET_PLACE}.`
    : `Then you can set up a passkey in ${FACTOR_RESET_PLACE}.`;
  const completed =
    holdsPasskey === false
      ? `Two-factor authentication has been turned off. ${SIGN_IN_AGAIN} ${nextStep}`
      : `Two-factor authentication has been turned off, and any passkeys on your account were removed. ${SIGN_IN_AGAIN} ${nextStep}`;
  return {
    title: 'Reset two-factor authentication',
    // The apps' approved request line, word for word, in the passkey reset's short paragraphs.
    request: [
      'We’ll email a code to your Givmo account address.',
      'There’s a 24-hour wait, so no one can do this quietly. We’ll email you when it starts, so you can cancel it.',
      `When the wait is over, come back and enter a new emailed code to finish turning off two-factor authentication.${passkeysToo}`,
    ],
    // C5, approved as written: the cancel line below names the place.
    started: (executesAt) => [
      `Two-factor authentication stays on until you finish. ${
        executesAt !== null
          ? `From ${formatFactorResetDeadline(executesAt)}, come back`
          : 'After the waiting period, come back'
      } and enter a new emailed code to turn it off. We’ve emailed you in case it wasn’t you.`,
      FACTOR_RESET_CANCEL_LINE,
    ],
    completedTitle: 'Two-factor authentication is off',
    completed,
    finishTitle: FACTOR_RESET_FINISH,
    // APPROVED (AS-WEB17; D-AS-39 E6, as written): the finish step's paragraphs.
    finish: [
      'The wait is over. We’ll email a code to your Givmo account address.',
      `Enter it to finish turning off two-factor authentication.${passkeysToo}`,
    ],
    notYetTitle: NOT_YET_TITLE,
    notYet: (executesAt) => [deadlineSentence(executesAt), FACTOR_RESET_CANCEL_LINE],
  };
}

/** One reset's words. */
export function factorResetWords(
  kind: FactorResetKind,
  options: FactorResetWordsOptions,
): FactorResetWords {
  return kind === 'twoFactor' ? twoFactorWords(options) : passkeyWords(options);
}

/** The card's notice for a pending reset (the apps' Today alert, approved). */
export interface PendingFactorResetWords {
  headline: string;
  /** Before the wait is over: who asked, and decision 3's deadline. */
  body: (executesAt: Date) => string;
  /**
   * Once the wait is over (AS-WEB17; APPROVED, D-AS-39 E8): a past date beside "Finish the reset"
   * would read as a mistake, so the deadline sentence says "now".
   */
  dueBody: string;
  /** The button: the label every channel names. */
  cancel: string;
  /** Said once the cancel went through. */
  cancelled: string;
  /** Once the wait is over: the button that finishes it (AS-WEB17; APPROVED, D-AS-39 E8). */
  finish: string;
}

/**
 * The pending reset's words, named by what the account has (decision 2): the two-factor alert when
 * it holds an authenticator app, else the passkey alert. The deadline sentence is decision 3's until
 * the wait is over, then "now" (APPROVED, D-AS-39 E8).
 */
export function pendingFactorResetWords(holdsAuthenticator: boolean): PendingFactorResetWords {
  const deadline = (executesAt: Date) => deadlineSentence(executesAt);
  // APPROVED (AS-WEB17; D-AS-39 E8, as written): the after-wait line beside "Finish the reset".
  const now = 'It can be finished now with a new code from your email, unless you cancel.';
  return holdsAuthenticator
    ? {
        headline: 'Two-factor reset requested',
        body: (executesAt) =>
          `Someone asked to turn off two-factor authentication on this account. ${deadline(executesAt)}`,
        dueBody: `Someone asked to turn off two-factor authentication on this account. ${now}`,
        cancel: 'Wasn’t me — cancel it',
        cancelled: 'Two-factor authentication will stay on.',
        finish: FACTOR_RESET_FINISH,
      }
    : {
        headline: 'Passkey reset requested',
        body: (executesAt) =>
          `Someone asked to remove the passkeys on this account. ${deadline(executesAt)}`,
        dueBody: `Someone asked to remove the passkeys on this account. ${now}`,
        cancel: 'Wasn’t me — cancel it',
        cancelled: 'Your passkeys stay on your account.',
        finish: FACTOR_RESET_FINISH,
      };
}

/**
 * Where "Lost your authenticator?" leads (D-AS-21's approved B2d-lost; AS-WEB18): its title and its
 * two ways out, as drawn and approved. What the lost authenticator means for the act at hand is the
 * row's (`StepUpLostLines`); these are the view's own.
 */
export const LOST_AUTHENTICATOR_WORDS = {
  title: 'Lost your authenticator?',
  recoveryCode: 'Use a recovery code',
  emailReset: 'Email me a reset',
  back: 'Back',
  /**
   * For an account whose read says none is left, the way out is drawn, and closed. APPROVED: copy
   * round 3's fix 8b (D-AS-39, Jonathan 2026-10-05) — it also reaches an account whose codes were
   * never created, where "left" would imply there were some.
   */
  noCodesLeft: 'You don’t have any recovery codes to use.',
} as const;

/**
 * The consoles' own lines where an act's row names none, every one APPROVED by copy round 3
 * (D-AS-39, Jonathan 2026-10-05): `recoveryCode` and `emailReset` as E2 and `recoveryCodeResult` as
 * E5 wrote them, and `line` as fix 8a reworded it. The approved frame's are Board's (signing);
 * these say what a lost authenticator means for a console's change: removed, a re-sign-in confirms
 * without a code. The reset's line is the frame's own first two sentences. `line`'s fix 8a:
 * removing the authenticator signs the account out everywhere, now on reload too (AS-BE63), so it
 * no longer says "You’re still signed in", and its second sentence is D-AS-35 #15's approved one.
 */
export const LOST_AUTHENTICATOR_LINES: Readonly<Required<StepUpLostLines>> = {
  line: 'To confirm without it, remove it from your account. You’ll be asked to sign in again, without a code.',
  recoveryCode: 'Removes the lost authenticator right away.',
  emailReset: 'No recovery codes? The reset takes 24 hours, and we tell you at each step.',
  // The backend removing it ends every Firebase session (the header): the person signs in again, codeless.
  recoveryCodeResult:
    'We’ve emailed you about it. You’ll be asked to sign in again, without a code. Then you can start your change again.',
};

/**
 * Spending a recovery code (AS-WEB18), every line APPROVED by copy round 3 (D-AS-39: the step as
 * E4 and the result as E5 wrote them): iOS's recovery step (`SecondFactorChallengeView`) where it
 * holds on the web — its title, its first sentence, its field and its button, all new here — and
 * the result, which says what the code removed, the authenticator app, and nothing else (it removes
 * no passkey).
 */
export const RECOVERY_CODE_WORDS = {
  title: 'Use a recovery code',
  line: 'Enter one of the recovery codes you saved when you turned on two-factor authentication.',
  label: 'Recovery code',
  submit: 'Use recovery code',
  back: 'Back',
  resultTitle: 'Your authenticator app was removed',
  done: 'Done',
} as const;
